import { createContext, useContext, useEffect, useState } from 'react';
import { fetchApplications } from '../services/applicationService';

// Fallback module configurations with their application IDs (for when API fails)
const FALLBACK_MODULE_CONFIGS = {
  'smart-assistant': {
    key: 'smart-assistant',
    name: 'AI Smart Assistant',
    applicationId: process.env.REACT_APP_SMART_ASSISTANT_APPLICATION_ID
  },
  'energy-optimization': {
    key: 'energy-optimization',
    name: 'Energy Optimization',
    applicationId: process.env.REACT_APP_ENERGY_OP_APPLICATION_ID
  },
  'focus360': {
    key: 'focus360',
    name: 'Focus 360',
    applicationId: process.env.REACT_APP_FOCUS_360_APPLICATION_ID
  },
  'oee': {
    key: 'oee',
    name: 'OEE Insights',
    applicationId: process.env.REACT_APP_OEE_INSIGHTS_APPLICATION_ID
  }
};

// Mapping from API response names to module keys
const API_NAME_TO_MODULE_KEY = {
  'AI Smart Assistant': 'smart-assistant',
  'Energy Optimization': 'energy-optimization',
  'Focus 360': 'focus360',
  'OEE Insights': 'oee'
};

// Function to map API applications to module configurations
const mapApplicationsToModuleConfigs = (applications) => {
  const configs = {};
  
  //  console.log('📋 Processing applications from API:');
  applications.forEach(app => {
    const moduleKey = API_NAME_TO_MODULE_KEY[app.name];
    
    if (!app.is_active) {
      // console.log(`❌ Skipping inactive application: ${app.name} (ID: ${app.id})`);
      return;
    }
    
    if (moduleKey) {
      configs[moduleKey] = {
        key: moduleKey,
        name: app.name,
        applicationId: app.id
      };
      // console.log(`✅ Added active module: ${app.name} -> ${moduleKey} (ID: ${app.id})`);
    } else {
      // console.log(`⚠️ Unknown application name: ${app.name} (ID: ${app.id}) - not mapped to any module`);
    }
  });
  
  // console.log(`🎯 Final active modules: ${Object.keys(configs).join(', ')}`);
  return configs;
};

const ModuleContext = createContext(undefined);

export const ModuleProvider = ({ children }) => {
  // Initialize selectedModule from localStorage or default to 'smart-assistant'
  const [selectedModule, setSelectedModule] = useState(() => {
    const savedModule = localStorage.getItem('selectedModule');
    return savedModule || 'smart-assistant';
  });
  const [moduleConfigs, setModuleConfigs] = useState(FALLBACK_MODULE_CONFIGS); // start with fallback
  const [isLoadingApplications, setIsLoadingApplications] = useState(true);
  const [applicationError, setApplicationError] = useState(null);

  // Save selectedModule to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('selectedModule', selectedModule);
  }, [selectedModule]);

  // Fetch applications from API on component mount
  useEffect(() => {
    const loadApplications = async () => {
      try {
        setIsLoadingApplications(true);
        setApplicationError(null);
        
        const applications = await fetchApplications();
        const dynamicConfigs = mapApplicationsToModuleConfigs(applications);
        
        // Use dynamic configs if we have any, otherwise keep fallback
        if (Object.keys(dynamicConfigs).length > 0) {
          setModuleConfigs(dynamicConfigs);
          // console.log('✅ Applications loaded successfully:', dynamicConfigs);
        } else {
          // console.warn('⚠️ No active applications found, using fallback configurations');
        }
      } catch (error) {
        // console.error('❌ Failed to fetch applications, using fallback configurations:', error);
        setApplicationError(error);
        // Keep using fallback configurations on error
      } finally {
        setIsLoadingApplications(false);
      }
    };

    loadApplications();
  }, []);

  // Ensure selectedModule is always an active module
  useEffect(() => {
    const availableModules = Object.keys(moduleConfigs);
    
    // If current selectedModule is not in available modules, switch to the first available one
    if (availableModules.length > 0 && !availableModules.includes(selectedModule)) {
      const fallbackModule = availableModules.includes('smart-assistant') 
        ? 'smart-assistant' 
        : availableModules[0];
      
      // console.log(`⚠️ Selected module '${selectedModule}' is not active, switching to '${fallbackModule}'`);
      setSelectedModule(fallbackModule);
    }
  }, [moduleConfigs, selectedModule]);

  const getCurrentApplicationId = () => {
    const config = moduleConfigs[selectedModule];
    if (!config) {
      //  console.warn(`Module configuration not found for: ${selectedModule}, using fallback`);
      return FALLBACK_MODULE_CONFIGS[selectedModule]?.applicationId;
    }
    return config.applicationId;
  };

  const getModuleConfig = (module) => {
    const targetModule = module || selectedModule;
    const config = moduleConfigs[targetModule];
    if (!config) {
      // console.warn(`Module configuration not found for: ${targetModule}, using fallback`);
      return FALLBACK_MODULE_CONFIGS[targetModule];
    }
    return config;
  };

  const getAllModuleConfigs = () => {
    return moduleConfigs;
  };

  const getApplicationIdForModule = (module) => {
    const config = moduleConfigs[module];
    if (!config) {
      // console.warn(`Module configuration not found for: ${module}, using fallback`);
      return FALLBACK_MODULE_CONFIGS[module]?.applicationId;
    }
    return config.applicationId;
  };

  // Get available module keys (only active modules)
  const getAvailableModules = () => {
    return Object.keys(moduleConfigs);
  };

  // Check if a module is active/available
  const isModuleActive = (moduleKey) => {
    return Object.keys(moduleConfigs).includes(moduleKey);
  };

  return (
    <ModuleContext.Provider
      value={{
        selectedModule,
        setSelectedModule,
        getCurrentApplicationId,
        getModuleConfig,
        getAllModuleConfigs,
        getApplicationIdForModule,
        getAvailableModules,
        isModuleActive,
        isLoadingApplications,
        applicationError,
      }}
    >
      {children}
    </ModuleContext.Provider>
  );
};

export const useModuleContext = () => {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModuleContext must be used within a ModuleProvider');
  }
  return context;
};

// Export the fallback module configurations for use in other parts of the app (for backward compatibility)
export { FALLBACK_MODULE_CONFIGS as MODULE_CONFIGS };
